import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, authedUrl, fmtCur, fmtNum, fmtPct } from "@/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Download } from "lucide-react";

export default function ShareholdingReport() {
  const [params, setParams] = useSearchParams();
  const [stocks, setStocks] = useState([]);
  const [selected, setSelected] = useState(params.get("symbol") || "");
  const [report, setReport] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/stocks");
      setStocks(data);
      if (!selected && data.length) setSelected(data[0].symbol);
    })();
  }, []);

  useEffect(() => {
    if (!selected) return;
    setParams({ symbol: selected });
    (async () => {
      const { data } = await api.get(`/stocks/${selected}/shareholding`);
      setReport(data);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps">Report</div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl mt-2 tracking-tight">Stock-Wise Shareholding</h1>
          <p className="text-slate-400 text-sm mt-2">Which of your clients own a stock, how many shares, and at what value.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          <div className="w-full sm:w-72">
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger data-testid="select-shareholding-stock" className="bg-slate-900/70 border-slate-800 h-11">
                <SelectValue placeholder="Select a stock" />
              </SelectTrigger>
              <SelectContent className="bg-[#0E1524] border-slate-800 text-white max-h-80">
                {stocks.length === 0 && <div className="p-4 text-sm text-slate-400">No stocks available.</div>}
                {stocks.map(s => <SelectItem key={s.symbol} value={s.symbol} data-testid={`select-stock-option-${s.symbol}`}>{s.symbol} {s.name ? `· ${s.name}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {selected && (
            <a href={authedUrl(`/stocks/${selected}/shareholding.csv`)} download data-testid="btn-download-shareholding-csv">
              <Button variant="outline" className="border-slate-700 text-slate-800 hover:text-emerald-400 h-11">
                <Download size={14} className="mr-2" /> CSV
              </Button>
            </a>
          )}
        </div>
      </header>

      {!report ? (
        <div className="card-tactical p-10 text-center text-slate-500">Select a stock to view shareholding report.</div>
      ) : (
        <>
          <section className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <MetricBox label="Symbol" value={report.stock.symbol} mono />
            <MetricBox label="Market Price" value={fmtCur(report.stock.market_price, report.stock.currency || "RM", 2)} mono />
            <MetricBox label="Total Shares Held" value={fmtNum(report.totals.total_shares, 2)} mono />
            <MetricBox label="Total Value" value={fmtCur(report.totals.total_value, report.stock.currency || "RM")} mono />
          </section>

          <section className="card-tactical p-6">
            <div className="flex items-center gap-3 mb-4">
              <Users size={18} className="text-emerald-400" />
              <h2 className="font-heading font-bold text-xl">Clients Holding {report.stock.symbol}</h2>
              <span className="label-caps ml-2">{report.totals.num_holders} holders</span>
            </div>
            {report.holders.length === 0 ? (
              <div className="py-10 text-center text-slate-500 border border-dashed border-slate-800 rounded-lg">No clients currently hold this stock.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="tactical" data-testid="table-shareholding-report-by-stock">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th className="!text-right">Quantity</th>
                      <th className="!text-right">% of Total</th>
                      <th className="!text-right">Avg Buy</th>
                      <th className="!text-right">Cost Basis</th>
                      <th className="!text-right">Current Value</th>
                      <th className="!text-right">Unrealized P&L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.holders.map(h => {
                      const cur = report.stock.currency || "RM";
                      return (
                      <tr key={h.client_id} data-testid={`shareholding-row-${h.client_id}`}>
                        <td><Link to={`/clients/${h.client_id}`} className="font-semibold hover:text-emerald-400">{h.client_name}</Link></td>
                        <td className="!text-right font-mono-tabular">{fmtNum(h.quantity, 2)}</td>
                        <td className="!text-right font-mono-tabular text-slate-400">{fmtPct(h.percent_of_total)}</td>
                        <td className="!text-right font-mono-tabular">{fmtCur(h.avg_buy_price, cur, 2)}</td>
                        <td className="!text-right font-mono-tabular">{fmtCur(h.cost_basis, cur)}</td>
                        <td className="!text-right font-mono-tabular">{fmtCur(h.current_value, cur)}</td>
                        <td className={`!text-right font-mono-tabular ${h.unrealized_pnl >= 0 ? "ticker-up" : "ticker-down"}`}>{fmtCur(h.unrealized_pnl, cur)}</td>
                      </tr>
                    );})}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-slate-700">
                      <td className="py-3 font-bold">TOTAL</td>
                      <td className="!text-right font-mono-tabular font-bold">{fmtNum(report.totals.total_shares, 2)}</td>
                      <td className="!text-right font-mono-tabular font-bold">100.00%</td>
                      <td />
                      <td className="!text-right font-mono-tabular font-bold">{fmtCur(report.totals.total_cost, report.stock.currency || "RM")}</td>
                      <td className="!text-right font-mono-tabular font-bold">{fmtCur(report.totals.total_value, report.stock.currency || "RM")}</td>
                      <td className={`!text-right font-mono-tabular font-bold ${report.totals.unrealized_pnl >= 0 ? "ticker-up" : "ticker-down"}`}>{fmtCur(report.totals.unrealized_pnl, report.stock.currency || "RM")}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

const MetricBox = ({ label, value, mono }) => (
  <div className="card-tactical p-5">
    <div className="label-caps">{label}</div>
    <div className={`text-2xl mt-2 ${mono ? "metric-value" : "font-heading font-bold"}`}>{value}</div>
  </div>
);
